import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CustomerHeader } from "@/components/CustomerHeader";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Gift, Camera, Heart, Sparkles, Loader2, ImageOff, X, ExternalLink, 
  Store, TrendingUp, Users, MapPin, ShoppingBag, Instagram, Twitter,
  Lightbulb, BookOpen, Star, ArrowRight, Hash
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface GiftPhoto {
  id: string;
  photo_url: string;
  caption: string | null;
  uploaded_by_name: string;
  uploaded_at: string;
  request_id: string;
}

// Pinterest-style inspiration with vibrant images
const INSPIRATION_ITEMS = [
  { id: 1, category: "Birthday", gradient: "from-rose-500 via-pink-500 to-fuchsia-500", emoji: "🎂", height: "h-72", subtitle: "Make it memorable" },
  { id: 2, category: "Wedding", gradient: "from-amber-400 via-yellow-300 to-lime-300", emoji: "💒", height: "h-56", subtitle: "Pool together for the big day" },
  { id: 3, category: "Baby Shower", gradient: "from-sky-400 via-cyan-300 to-teal-400", emoji: "👶", height: "h-64", subtitle: "Welcome the little one" },
  { id: 4, category: "Housewarming", gradient: "from-orange-500 via-amber-400 to-yellow-400", emoji: "🏡", height: "h-52", subtitle: "New beginnings" },
  { id: 5, category: "Anniversary", gradient: "from-red-500 via-rose-400 to-pink-400", emoji: "💕", height: "h-68", subtitle: "Celebrate love" },
  { id: 6, category: "Graduation", gradient: "from-violet-600 via-purple-500 to-fuchsia-500", emoji: "🎓", height: "h-48", subtitle: "Achievement unlocked" },
  { id: 7, category: "Retirement", gradient: "from-emerald-500 via-teal-400 to-cyan-400", emoji: "🌴", height: "h-60", subtitle: "Next chapter awaits" },
  { id: 8, category: "Thank You", gradient: "from-yellow-400 via-amber-300 to-orange-300", emoji: "💐", height: "h-52", subtitle: "Show appreciation" },
  { id: 9, category: "Get Well", gradient: "from-green-400 via-emerald-300 to-teal-300", emoji: "🌻", height: "h-56", subtitle: "Send love & healing" },
  { id: 10, category: "Festivals", gradient: "from-fuchsia-500 via-pink-400 to-rose-400", emoji: "🪔", height: "h-72", subtitle: "Celebrate together" },
  { id: 11, category: "Holiday", gradient: "from-red-600 via-red-500 to-green-500", emoji: "🎄", height: "h-48", subtitle: "Season of giving" },
  { id: 12, category: "Just Because", gradient: "from-indigo-500 via-purple-400 to-pink-400", emoji: "✨", height: "h-60", subtitle: "No reason needed" },
];

// Featured stores with more personality
const FEATURED_STORES = [
  { id: 1, name: "Gift Bazaar", emoji: "🎁", tagline: "Curated gifts", color: "from-pink-500 to-rose-500" },
  { id: 2, name: "Tech Haven", emoji: "💻", tagline: "Gadgets & more", color: "from-blue-500 to-cyan-500" },
  { id: 3, name: "Home Bliss", emoji: "🏠", tagline: "Cozy essentials", color: "from-amber-500 to-orange-500" },
  { id: 4, name: "Style Studio", emoji: "👗", tagline: "Fashion forward", color: "from-purple-500 to-violet-500" },
  { id: 5, name: "Toy Kingdom", emoji: "🧸", tagline: "Play & wonder", color: "from-green-500 to-emerald-500" },
  { id: 6, name: "Gourmet Corner", emoji: "🍫", tagline: "Delicious treats", color: "from-red-500 to-pink-500" },
];

const TRENDING_HASHTAGS = [
  "#GroupBuyingWithShakatah",
  "#SplitTheJoy",
  "#TogetherWeBuy",
  "#ShareTheLove",
  "#GiftGoals",
];

const GiftGallery = () => {
  const [selectedPhoto, setSelectedPhoto] = useState<GiftPhoto | null>(null);

  const { data: photos, isLoading } = useQuery({
    queryKey: ["gallery-photos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gift_photos")
        .select("*")
        .order("uploaded_at", { ascending: false })
        .limit(6);

      if (error) throw error;
      return data as GiftPhoto[];
    },
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-purple-50/30 to-pink-50/20 dark:from-background dark:via-purple-950/10 dark:to-pink-950/10">
      <SEO
        title="Discover | Group Purchase Community"
        description="Find stores with group purchase, explore ideas, and join the #GroupPurchaseWithShakatah community!"
        url="/gallery"
      />
      <CustomerHeader />

      <main className="pb-8">
        {/* Hero Section - Fun & Colorful */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-customer/20 via-purple-500/10 to-pink-500/10" />
          <div className="absolute top-10 left-10 w-20 h-20 bg-yellow-400/20 rounded-full blur-2xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-32 h-32 bg-pink-400/20 rounded-full blur-3xl animate-pulse" />
          
          <div className="container relative py-8 md:py-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-customer/20 to-pink-500/20 text-customer text-sm font-medium mb-4 animate-fade-in">
              <Sparkles className="w-4 h-4" />
              Discover • Share • Celebrate
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
              Group Purchase <span className="bg-gradient-to-r from-customer via-purple-500 to-pink-500 bg-clip-text text-transparent">Community</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto mb-6">
              Find stores, get inspired, and see what others are gifting together!
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/my-purchases">
                <Button size="lg" className="bg-customer hover:bg-customer/90 text-customer-foreground shadow-lg hover:shadow-xl transition-all">
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Track Your Purchases
                </Button>
              </Link>
              <Button 
                size="lg"
                variant="outline"
                className="border-customer/30 hover:bg-customer/10"
                onClick={() => window.open('https://www.instagram.com/explore/tags/groupgiftingwithshakatah/', '_blank')}
              >
                <Instagram className="w-5 h-5 mr-2 text-pink-500" />
                Share Your Story
              </Button>
            </div>
          </div>
        </section>

        {/* Pinterest-Style Inspiration Grid */}
        <section className="container py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 text-white">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Get Inspired</h2>
              <p className="text-sm text-muted-foreground">Ideas for every occasion</p>
            </div>
          </div>

          {/* Masonry-style Pinterest Grid */}
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {INSPIRATION_ITEMS.map((item) => (
              <Card 
                key={item.id}
                className="group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 break-inside-avoid mb-4"
              >
                <div className={`${item.height} bg-gradient-to-br ${item.gradient} flex flex-col items-center justify-center relative overflow-hidden`}>
                  {/* Decorative elements */}
                  <div className="absolute inset-0 bg-white/5" />
                  <div className="absolute top-4 left-4 w-20 h-20 bg-white/20 rounded-full blur-2xl" />
                  <div className="absolute bottom-4 right-4 w-24 h-24 bg-white/15 rounded-full blur-3xl" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white/10 rounded-full blur-xl" />
                  
                  <span className="text-6xl md:text-7xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 drop-shadow-xl relative z-10">
                    {item.emoji}
                  </span>
                  <span className="mt-3 text-white font-bold text-xl tracking-wide relative z-10 drop-shadow-md">
                    {item.category}
                  </span>
                  <span className="text-white/70 text-xs relative z-10 mt-1">
                    {item.subtitle}
                  </span>
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                    <Button size="sm" className="bg-white text-foreground hover:bg-white/90 shadow-lg">
                      <Sparkles className="w-4 h-4 mr-1" />
                      Explore Ideas
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Trending Stores - Simplified */}
        <section className="container py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">Trending Stores</h2>
                <p className="text-sm text-muted-foreground">Popular picks this week</p>
              </div>
            </div>
            <Badge variant="secondary">
              <Store className="w-3 h-3 mr-1" /> Coming Soon
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {FEATURED_STORES.map((store) => (
              <Card 
                key={store.id} 
                className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-0 overflow-hidden"
              >
                <CardContent className="p-0">
                  <div className={`bg-gradient-to-br ${store.color} p-4 text-center`}>
                    <span className="text-3xl block mb-1">{store.emoji}</span>
                    <span className="font-bold text-white text-sm block">{store.name}</span>
                    <span className="text-white/70 text-xs">{store.tagline}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Latest Purchases & Social Feed */}
        <section className="container py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Latest Purchases */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 text-white">
                  <Gift className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">Latest Group Purchases</h2>
                  <p className="text-sm text-muted-foreground">Real-time community activity</p>
                </div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-customer" />
                </div>
              ) : !photos || photos.length === 0 ? (
                <Card className="border-dashed border-2">
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-customer/20 to-purple-500/20 flex items-center justify-center mx-auto mb-4">
                      <Camera className="w-8 h-8 text-customer" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">No photos yet!</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Be the first to share your group purchase moment
                    </p>
                    <Link to="/my-purchases">
                      <Button size="sm" className="bg-customer hover:bg-customer/90">
                        <Gift className="w-4 h-4 mr-2" />
                        View Your Purchases
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {photos.map((photo) => (
                    <Card
                      key={photo.id}
                      className="overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300 border-2 hover:border-customer/30"
                      onClick={() => setSelectedPhoto(photo)}
                    >
                      <div className="relative aspect-square">
                        <img
                          src={photo.photo_url}
                          alt={photo.caption || "Gift moment"}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="absolute bottom-0 left-0 right-0 p-3 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                          <p className="font-medium text-sm truncate">{photo.uploaded_by_name}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Social Sidebar */}
            <div className="space-y-6">
              {/* Hashtags */}
              <Card className="border-2 border-pink-200 dark:border-pink-900/30 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950/20 dark:to-purple-950/20">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Hash className="w-5 h-5 text-pink-500" />
                    <h3 className="font-bold text-foreground">Trending Hashtags</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {TRENDING_HASHTAGS.map((tag, index) => (
                      <Button
                        key={index}
                        variant="secondary"
                        size="sm"
                        className="text-xs bg-white/50 dark:bg-white/10 hover:bg-customer/10 hover:text-customer transition-all"
                        onClick={() => {
                          const searchTag = tag.replace('#', '');
                          window.open(`https://www.instagram.com/explore/tags/${searchTag}/`, '_blank');
                        }}
                      >
                        {tag}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Share CTA */}
              <Card className="bg-gradient-to-br from-customer to-purple-600 text-white border-0 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-2xl" />
                <CardContent className="p-5 relative">
                  <Sparkles className="w-8 h-8 mb-3 text-yellow-300" />
                  <h3 className="font-bold text-lg mb-2">Share Your Moment!</h3>
                  <p className="text-sm text-white/80 mb-4">
                    Received an amazing group gift? Share with the community!
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-white text-customer hover:bg-white/90 flex-1"
                      onClick={() => window.open('https://www.instagram.com/explore/tags/groupgiftingwithshakatah/', '_blank')}
                    >
                      <Instagram className="w-4 h-4 mr-1" />
                      Instagram
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/20 hover:bg-white/30 text-white border-0 flex-1"
                      onClick={() => window.open('https://twitter.com/search?q=%23GroupGiftingWithShakatah', '_blank')}
                    >
                      <Twitter className="w-4 h-4 mr-1" />
                      Twitter
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Stats */}
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-500" />
                    Community Stats
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Happy Gifters</span>
                      <span className="font-bold text-customer">12,450+</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Group Purchases</span>
                      <span className="font-bold text-customer">8,230+</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Partner Stores</span>
                      <span className="font-bold text-customer">500+</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      {/* Photo Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {selectedPhoto && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full"
                onClick={() => setSelectedPhoto(null)}
              >
                <X className="w-5 h-5" />
              </Button>
              <img
                src={selectedPhoto.photo_url}
                alt={selectedPhoto.caption || "Gift moment"}
                className="w-full max-h-[70vh] object-contain bg-black"
              />
              <div className="p-6 bg-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">{selectedPhoto.uploaded_by_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(selectedPhoto.uploaded_at), "MMMM d, yyyy")}
                    </p>
                  </div>
                  <Link to={`/split/${selectedPhoto.request_id}`}>
                    <Button variant="outline" size="sm">
                      View Purchase <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                </div>
                {selectedPhoto.caption && (
                  <p className="mt-4 text-muted-foreground">{selectedPhoto.caption}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="container py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-customer" />
              <span className="font-bold text-foreground">Shakatah</span>
              <span className="text-muted-foreground text-sm ml-2">Group purchases made simple</span>
            </div>
            <div className="flex items-center gap-4 md:gap-6 text-sm text-muted-foreground">
              <Link to="/my-purchases" className="hover:text-foreground transition-colors">My Purchases</Link>
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <a href="/#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GiftGallery;